import { useAtom, useAtomValue } from "jotai";
import { MouseEvent, useEffect, useMemo, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Button } from "chameleon/lib/components";
import { useExpandCollapseAnimation } from "chameleon/lib/hooks/use-expand-collapse-animation";

import {
  paymentSummaryAtom,
  showCardNumberSpinnerAtom,
} from "checkout-experience/atoms/payment-atoms";
import { FormTitle } from "checkout-experience/components/forms/form-title";
import PaymentMethodSummaryDisplay from "checkout-experience/components/payment-method/payment-method-summary-display";
import PlaceOrder from "checkout-experience/components/place-order/place-order";
import { useGeneratePaymentSummary } from "checkout-experience/hooks/use-generate-payment-summary";
import { useIsCreditCardWithSurchargeChex } from "checkout-experience/hooks/use-is-credit-card-with-surcharge-chex";
import { useIsExpired } from "checkout-experience/hooks/use-is-expired";
import { useSecurityCodeRequired } from "checkout-experience/hooks/use-security-code-required";
import { useSurchargeText } from "checkout-experience/hooks/use-surcharge";
import { useChexContext } from "checkout-experience/providers/chex-context-wrapper";
import { PaymentDomain } from "checkout-query-client/api/types";
import {
  FormErrorCode,
  PaymentType,
} from "checkout-query-client/types/form-types";

import { PayRequestErrorCode } from "frontend-client";

import ErrorBlock from "../error-block/error-block";
import { FormEditButton } from "../forms/form-edit-button";
import { marginTopClass } from "../place-order/place-order.css";
import CombinedPaymentMethodsList from "./combined-payment-methods-list";
import { ApplePay } from "./pay-options/apple/apple-pay";
import { contentSection } from "./payment-method.css";
import { usePaymentOptionContext } from "./payment-option-provider";

interface PaymentMethodProps {
  isPayInProgress: boolean;
  paymentError?: PayRequestErrorCode;
  paymentErrorDetailMessage?: string;
  onSubmit: () => Promise<void>;
  paymentDomain?: PaymentDomain;
  applePayComponent: JSX.Element | null;
  setApplePayComponent: (el: JSX.Element | null) => void;
  setShowPlaceOrderSection: (show: boolean) => void;
}

export enum PaymentOptionsView {
  COLLAPSE,
  EXPAND,
  VALIDATING,
}

const PaymentMethod = ({
  isPayInProgress,
  paymentError,
  onSubmit,
  paymentDomain,
  applePayComponent,
  setApplePayComponent,
  setShowPlaceOrderSection,
}: PaymentMethodProps) => {
  const { t } = useTranslation("combined");
  const translatedTitle = t("checkoutForm.submitButton.continue");
  const paymentMethodSummary = useAtomValue(paymentSummaryAtom);
  const { paymentOption } = usePaymentOptionContext() ?? {};
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [showPaymentOptions, setShowPaymentOptions] =
    useState<PaymentOptionsView>(PaymentOptionsView.COLLAPSE);
  const [editSavedPaymentMethod, setEditSavedPaymentMethod] = useState(false);
  const { experienceType } = useChexContext();
  const securityCodeRequired = useSecurityCodeRequired(
    paymentOption?.paymentType
  );
  const surchargeText = useSurchargeText();
  const isExpired = useIsExpired();
  const checkoutForm = useFormContext();
  const { trigger } = checkoutForm;
  const isSavedPaymentMethod =
    paymentOption?.formId !== paymentOption?.paymentType;
  const [showCardTypeSpinner, setShowCardTypeSpinner] = useAtom(
    showCardNumberSpinnerAtom
  );
  const { isFetching: gettingIsCreditCard, isError: isCreditCardError } =
    useIsCreditCardWithSurchargeChex(checkoutForm);

  const showTitle = useMemo(() => {
    return (
      experienceType !== "dropInUI" ||
      (experienceType === "dropInUI" &&
        showPaymentOptions === PaymentOptionsView.COLLAPSE)
    );
  }, [experienceType, showPaymentOptions]);

  useGeneratePaymentSummary();

  useExpandCollapseAnimation({
    componentRef: contentRef,
    expand: showPaymentOptions === PaymentOptionsView.EXPAND,
  });

  const onClickEdit = (e?: MouseEvent) => {
    e?.preventDefault();
    setShowPaymentOptions(PaymentOptionsView.EXPAND);
    if (setApplePayComponent) setApplePayComponent(null);
  };

  const handleClickContinue = async () => {
    if (gettingIsCreditCard) {
      setShowCardTypeSpinner(true);
    } else {
      if (paymentOption?.paymentType === PaymentType.applePay) {
        setApplePayComponent(<ApplePay paymentDomain={paymentDomain} />);
      }
      const isValid = await trigger(`${paymentOption?.formId}`, {
        shouldFocus: true,
      });
      setShowPaymentOptions(
        isValid ? PaymentOptionsView.COLLAPSE : PaymentOptionsView.EXPAND
      );
      if (isSavedPaymentMethod) {
        setEditSavedPaymentMethod(!isValid);
      }
    }
  };

  useEffect(() => {
    if (experienceType === "dropInUI") {
      setApplePayComponent(<ApplePay paymentDomain={paymentDomain} />);
    } else {
      setApplePayComponent(null);
    }
  }, []);

  useEffect(() => {
    setShowPlaceOrderSection(
      showPaymentOptions === PaymentOptionsView.COLLAPSE
    );
  }, [showPaymentOptions]);

  useEffect(() => {
    if (!isSavedPaymentMethod || editSavedPaymentMethod || isExpired) {
      setShowPaymentOptions(PaymentOptionsView.EXPAND);
    } else if (paymentOption?.formId === "") {
      // WALLETs only
      setShowPaymentOptions(PaymentOptionsView.EXPAND);
    } else {
      setShowPaymentOptions(PaymentOptionsView.COLLAPSE);
    }
  }, [isSavedPaymentMethod, editSavedPaymentMethod, isExpired]);

  useEffect(() => {
    if (paymentOption?.formId === paymentOption?.paymentType) {
      setEditSavedPaymentMethod(false);
    }
  }, [paymentOption]);

  return (
    <>
      {experienceType === "dropInUI" && (
        <ErrorBlock
          paymentError={paymentError}
          formError={
            isCreditCardError ? FormErrorCode.cardTypeError : undefined
          }
        />
      )}
      <span id="paymentMethodSectionHeaderId" hidden>
        {t("paymentMethod.title")}
      </span>
      {showTitle ? (
        <FormTitle title={t("paymentMethod.title")} hasPaddingBottom />
      ) : null}
      <div
        id="payment-methods"
        data-testid="payment-methods"
        data-expanded={showPaymentOptions === PaymentOptionsView.EXPAND}
      >
        {showPaymentOptions === PaymentOptionsView.COLLAPSE &&
        paymentMethodSummary ? (
          <PaymentMethodSummaryDisplay
            paymentMethodSummary={paymentMethodSummary}
            isExpired={isExpired}
            showForm={securityCodeRequired && isSavedPaymentMethod}
            editSavedPaymentMethod={editSavedPaymentMethod}
            trailingSlot={
              showPaymentOptions === PaymentOptionsView.COLLAPSE ? (
                <FormEditButton onClickEdit={onClickEdit} />
              ) : undefined
            }
            surchargeText={surchargeText}
          />
        ) : (
          <div ref={contentRef} className={contentSection}>
            <CombinedPaymentMethodsList
              editSavedPaymentMethod={editSavedPaymentMethod}
              setEditSavedPaymentMethod={setEditSavedPaymentMethod}
              surchargeText={surchargeText}
              paymentDomain={paymentDomain}
              setApplePayComponent={setApplePayComponent}
            />
          </div>
        )}
      </div>
      {(experienceType === "hpp" || experienceType === "hppLink") &&
        showPaymentOptions === PaymentOptionsView.EXPAND && (
          <div className={marginTopClass}>
            <Button
              id={"btn-continue"}
              text={translatedTitle}
              testId={"btn-continue"}
              position="left"
              isLoading={isPayInProgress || showCardTypeSpinner}
              isDisabled={isPayInProgress || showCardTypeSpinner}
              onClick={handleClickContinue}
            />
          </div>
        )}
      {/** Global pay button for drop in ui */}
      {experienceType === "dropInUI" && (
        <PlaceOrder
          isPayInProgress={isPayInProgress}
          onClick={onSubmit}
          showAction={true} // always show on drop in ui
          applePayComponent={applePayComponent}
          paymentDomain={paymentDomain}
        />
      )}
    </>
  );
};
export default PaymentMethod;
